import { useContext, useEffect } from 'react';

import {
  STUDIO_CLIENT_PREVIEW_PARAM,
  STUDIO_CLIENT_PREVIEW_VALUE,
} from 'src/constants/studioFtux';
import { RouteContext } from 'src/context';
import history from 'src/history';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { setStudioClientPreview } from 'src/store/ui/actions';
import { selectIsStudioClientPreview } from 'src/store/ui/selectors';

/**
 * True while the current session is a Studio "test as a client" preview — the
 * landing URL carries `studioClientPreview=true` (minted by the CRM "Test as a
 * client" link). The intent is captured into the (unpersisted) store so the
 * client-side "Getting started" card shows during the preview only.
 *
 * The flag lives in the store rather than component state for two reasons the
 * mobile client portal exposes: the temporary sidebar drawer mounts its own,
 * separate SidebarNav instance, and it remounts across route transitions — a
 * component-local `useState` there would never see the flag the first instance
 * captured, or would reset on remount. A shared store value avoids both. It is
 * not persisted, so a full refresh clears it; the param is stripped from the URL
 * on capture so a refresh can't re-set it.
 */
export const useStudioClientPreview = (): boolean => {
  const { query } = useContext(RouteContext);
  const dispatch = useAppDispatch();
  const isStudioClientPreview = useAppSelector(selectIsStudioClientPreview);

  useEffect(() => {
    if (query[STUDIO_CLIENT_PREVIEW_PARAM] !== STUDIO_CLIENT_PREVIEW_VALUE) {
      return;
    }
    dispatch(setStudioClientPreview(true));
    const params = new URLSearchParams(history.location.search);
    params.delete(STUDIO_CLIENT_PREVIEW_PARAM);
    history.replace({ search: params.toString() });
  }, [query, dispatch]);

  return isStudioClientPreview;
};
